import {
  ConfigError,
  getConnectionConfigSchema,
} from '@gl-demo-ultimate-odupre/third-party-connections-shared';
import { z } from 'zod';

/**
 * Default Anthropic API base URL.
 *
 * Override `baseUrl` in {@link ClaudeConfig} only when an enterprise proxy or
 * a regional Anthropic endpoint is required. The shared
 * {@link ConnectionConfig} schema validates that `baseUrl` is a parseable URL.
 */
export const DEFAULT_CLAUDE_BASE_URL = 'https://api.anthropic.com';

/**
 * Default `anthropic-version` header pinned to the public-stable revision.
 *
 * Bumping this is a breaking change for downstream consumers and must be
 * gated behind a separate hardening story (per Challenger note on issue #1).
 */
export const DEFAULT_ANTHROPIC_VERSION = '2023-06-01';

/**
 * Zod schema for {@link ClaudeConfig}.
 *
 * Validates the Anthropic-specific fields on top of the shared
 * `connectionConfigSchema`. `baseUrl` defaults to
 * {@link DEFAULT_CLAUDE_BASE_URL} and `anthropicVersion` defaults to
 * {@link DEFAULT_ANTHROPIC_VERSION} when omitted.
 *
 * Use {@link ClaudeConfig} as the inferred type via `z.infer`.
 *
 * @example
 * ```ts
 * import { claudeConfigSchema } from '@gl-demo-ultimate-odupre/connect-with-claude';
 *
 * const parsed = claudeConfigSchema.parse({
 *   provider: 'claude',
 *   auth: { type: 'api_key', key: 'sk-ant-...' },
 *   model: 'claude-3-5-sonnet-latest',
 *   maxTokens: 256,
 * });
 * console.log(parsed.baseUrl); // -> 'https://api.anthropic.com'
 * ```
 */
export const claudeConfigSchema = getConnectionConfigSchema()
  .extend({
    provider: z.literal('claude'),
    auth: z.object({
      type: z.literal('api_key'),
      key: z.string().min(1),
      headerName: z.string().optional(),
    }),
    baseUrl: z.string().url().default(DEFAULT_CLAUDE_BASE_URL),
    model: z.string().min(1),
    maxTokens: z.number().int().positive(),
    temperature: z.number().min(0).max(1).optional(),
    anthropicVersion: z.string().min(1).default(DEFAULT_ANTHROPIC_VERSION),
  });

/**
 * Provider-specific configuration for `ClaudeProvider`.
 *
 * `ClaudeConfig` is the inferred (post-default) output of
 * {@link claudeConfigSchema}; the schema is the single source of truth so the
 * compile-time shape and runtime validator can never drift.
 *
 * The schema:
 * - narrows `provider` to the literal `'claude'`,
 * - narrows `auth` to {@link import('@gl-demo-ultimate-odupre/third-party-connections-shared').ApiKeyAuth} (Anthropic only accepts API-key auth),
 * - applies {@link DEFAULT_CLAUDE_BASE_URL} when `baseUrl` is omitted,
 * - applies {@link DEFAULT_ANTHROPIC_VERSION} when `anthropicVersion` is omitted.
 *
 * @example
 * ```ts
 * import {
 *   claudeConfigSchema,
 *   ClaudeConfig,
 * } from '@gl-demo-ultimate-odupre/connect-with-claude';
 *
 * const config: ClaudeConfig = claudeConfigSchema.parse({
 *   provider: 'claude',
 *   auth: { type: 'api_key', key: process.env.ANTHROPIC_API_KEY! },
 *   model: 'claude-3-5-sonnet-latest',
 *   maxTokens: 1024,
 *   temperature: 0.2,
 * });
 * ```
 */
export type ClaudeConfig = z.infer<typeof claudeConfigSchema>;

/**
 * Validate an unknown input against {@link claudeConfigSchema} and return a
 * fully-typed {@link ClaudeConfig} (with schema defaults applied).
 *
 * Throws a shared {@link ConfigError} (provider `'claude'`) on failure so
 * callers can pattern-match the same error class used by every other
 * connector. The thrown error carries `validationErrors` with one entry per
 * Zod issue (`<path>: <message>`).
 *
 * Prefer this helper over calling `claudeConfigSchema.parse(...)` directly
 * because the wrapped error type is what the rest of the connector ecosystem
 * already consumes (loggers, telemetry, UI surfaces).
 *
 * @example
 * ```ts
 * import { validateClaudeConfig } from '@gl-demo-ultimate-odupre/connect-with-claude';
 *
 * const cfg = validateClaudeConfig({
 *   provider: 'claude',
 *   auth: { type: 'api_key', key: process.env.ANTHROPIC_API_KEY! },
 *   model: 'claude-3-5-sonnet-latest',
 *   maxTokens: 256,
 * });
 * ```
 */
export function validateClaudeConfig(input: unknown): ClaudeConfig {
  const result = claudeConfigSchema.safeParse(input);
  if (result.success) {
    return result.data;
  }
  const errors = result.error.issues.map(
    (issue) => `${issue.path.join('.')}: ${issue.message}`,
  );
  throw new ConfigError({
    message: `Invalid configuration for claude: ${errors.join('; ')}`,
    provider: 'claude',
    validationErrors: errors,
  });
}

/**
 * Type guard that narrows `value` to {@link ClaudeConfig}.
 *
 * Performs a `safeParse` against {@link claudeConfigSchema}; defaults applied
 * by the schema are not back-propagated to `value` because guards are
 * non-mutating. Use {@link claudeConfigSchema.parse} or
 * `validateConfig(claudeConfigSchema, value, 'claude')` when you need the
 * canonicalised object.
 *
 * @example
 * ```ts
 * import { isClaudeConfig } from '@gl-demo-ultimate-odupre/connect-with-claude';
 *
 * function describe(input: unknown): string {
 *   if (isClaudeConfig(input)) {
 *     return `claude/${input.model}`;
 *   }
 *   return 'unknown';
 * }
 * ```
 */
export function isClaudeConfig(value: unknown): value is ClaudeConfig {
  return claudeConfigSchema.safeParse(value).success;
}
